 /* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import Header from "../components/Header";
import { ArrowLeft, CheckCircle, Circle, Clock, Target, Pencil, FloppyDisk, X, Confetti, Plus } from "@phosphor-icons/react";
import { motion } from "framer-motion";

function MaterialsEditor({ materials, onChange }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const value = draft.trim();
    if (!value) return;
    onChange([...materials, value]);
    setDraft("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {materials.length === 0 && (
          <span className="text-sm text-slate-400 italic">No materials yet — add some below.</span>
        )}
        {materials.map((m, idx) => (
          <span
            key={`${m}-${idx}`}
            className="inline-flex items-center gap-1 pl-3 pr-1.5 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm"
            data-testid={`edit-material-${idx}`}
          >
            {m}
            <button
              onClick={() => onChange(materials.filter((_, i) => i !== idx))}
              data-testid={`edit-material-remove-${idx}`}
              className="ml-1 p-0.5 rounded-full hover:bg-slate-200 text-slate-500 hover:text-red-500"
              title="Remove"
            >
              <X size={14} weight="bold" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder="e.g. Pencils, Paper, Webcam..."
          data-testid="edit-material-input"
          className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
        />
        <button
          onClick={add}
          data-testid="edit-material-add"
          className="px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold inline-flex items-center gap-1 hover:bg-slate-800"
        >
          <Plus size={14} weight="bold" /> Add
        </button>
      </div>
    </div>
  );
}

export default function WeekDetail() {
  const { weekNum } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [week, setWeek] = useState(null);
  const [progress, setProgress] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const isStaff = user?.role === "teacher" || user?.role === "assistant";
  const canEdit = !!(user?.is_admin || user?.can_edit_curriculum);

  const load = async () => {
    const [w, p] = await Promise.all([
      api.get(`/weeks/${weekNum}`),
      api.get(`/progress`).catch(() => ({ data: [] })),
    ]);
    setWeek(w.data);
    setProgress(p.data || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekNum]);

  // Each task is in one of three states for the student UI:
  //   - 'empty'    → never submitted (open circle, tap to submit)
  //   - 'pending'  → submitted, awaiting teacher verification (amber clock)
  //   - 'verified' → teacher approved (green check, line-through)
  const taskState = (idx) => {
    const row = progress.find(
      (p) => p.week_num === Number(weekNum) && p.task_index === idx,
    );
    if (!row) return "empty";
    return row.verified_at ? "verified" : "pending";
  };
  const isTaskDone = (idx) => taskState(idx) !== "empty";

  const toggleTask = async (idx) => {
    if (isStaff) return;
    const state = taskState(idx);
    // Block un-submit once verified — students shouldn't be able to roll
    // back a teacher's approval; ask the teacher in person if needed.
    if (state === "verified") return;
    const { data } = await api.post(`/progress/toggle`, { week_num: Number(weekNum), task_index: idx });
    if (data.submitted) {
      setProgress([
        ...progress,
        {
          week_num: Number(weekNum),
          task_index: idx,
          submitted_at: new Date().toISOString(),
          verified_at: null,
        },
      ]);
    } else {
      setProgress(progress.filter((p) => !(p.week_num === Number(weekNum) && p.task_index === idx)));
    }
  };

  const startEdit = () => {
    setEditForm({
      title: week.title,
      tagline: week.tagline,
      core_concept: week.core_concept,
      success_check: week.success_check,
      tasks: [...week.tasks],
      materials: [...week.materials],
      sessions: week.sessions.map((s) => ({ ...s })),
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(`/weeks/${weekNum}`, editForm);
      setWeek(data);
      setEditing(false);
    } finally { setBusy(false); }
  };

  if (!week) return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 text-slate-500" data-testid="week-loading">Loading week...</div>
    </div>
  );

  const isExhibition = week.week_num === 10;
  const verifiedHere = week.tasks.filter((_, i) => taskState(i) === "verified").length;
  const pendingHere = week.tasks.filter((_, i) => taskState(i) === "pending").length;

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        <button
          onClick={() => navigate(-1)}
          data-testid="week-back-btn"
          className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </button>

        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-3xl p-6 sm:p-8 border ${
            isExhibition
              ? "bg-gradient-to-br from-amber-50 via-pink-50 to-yellow-50 border-amber-200"
              : "bg-white border-slate-200"
          } shadow-sm`}
          data-testid="week-hero"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-2">
                {isExhibition && <Confetti weight="fill" className="text-pink-500" size={22} />}
                <span className="text-xs font-bold tracking-wider uppercase text-slate-500">
                  Week {week.week_num} · {week.category}
                </span>
              </div>
              {editing ? (
                <input
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  data-testid="edit-title-input"
                  className="font-outfit text-3xl sm:text-4xl font-black tracking-tight bg-transparent border-b-2 border-yellow-400 focus:outline-none w-full"
                />
              ) : (
                <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">{week.title}</h1>
              )}
              {editing ? (
                <input
                  value={editForm.tagline}
                  onChange={(e) => setEditForm({ ...editForm, tagline: e.target.value })}
                  data-testid="edit-tagline-input"
                  className="font-caveat text-2xl text-yellow-700 mt-2 bg-transparent border-b border-slate-200 w-full focus:outline-none"
                />
              ) : (
                <p className="font-caveat text-2xl sm:text-3xl text-yellow-700 mt-1">{week.tagline}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700">
                  <Clock size={14} weight="bold" /> {week.duration}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700">
                  <Target size={14} weight="bold" /> {week.tasks.length} tasks
                </span>
                {!isStaff && (
                  <>
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-emerald-100 text-emerald-800" data-testid="week-verified-pill">
                      <CheckCircle size={14} weight="fill" /> {verifiedHere}/{week.tasks.length} verified
                    </span>
                    {pendingHere > 0 && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-lg bg-amber-100 text-amber-900" data-testid="week-pending-pill">
                        <Clock size={14} weight="fill" /> {pendingHere} pending review
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>

            {canEdit && !editing && (
              <button
                onClick={startEdit}
                data-testid="week-edit-btn"
                className="btn-tactile-white inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 font-outfit font-semibold text-sm hover:bg-slate-50"
              >
                <Pencil size={16} weight="bold" /> Edit week
              </button>
            )}
            {editing && (
              <div className="flex gap-2">
                <button
                  onClick={() => setEditing(false)}
                  data-testid="week-cancel-edit-btn"
                  className="px-3 py-2 rounded-xl bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-1"
                >
                  <X size={16} /> Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={busy}
                  data-testid="week-save-edit-btn"
                  className="btn-tactile-yellow px-3 py-2 rounded-xl bg-yellow-400 text-yellow-950 text-sm font-bold hover:bg-yellow-500 inline-flex items-center gap-1"
                >
                  <FloppyDisk size={16} weight="bold" /> Save
                </button>
              </div>
            )}
          </div>
        </motion.div>

        {/* Core concept */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm">
          <h3 className="font-outfit font-bold text-lg mb-2">Core concept</h3>
          {editing ? (
            <textarea
              rows={3}
              value={editForm.core_concept}
              onChange={(e) => setEditForm({ ...editForm, core_concept: e.target.value })}
              data-testid="edit-concept-input"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-base"
            />
          ) : (
            <p className="text-slate-700 leading-relaxed">{week.core_concept}</p>
          )}
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="tasks-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-bold text-xl">Your next steps</h3>
            {!isStaff && (
              <span className="text-xs text-slate-500">Tap to submit · teacher verifies</span>
            )}
          </div>
          <ul className="space-y-2">
            {(editing ? editForm.tasks : week.tasks).map((task, idx) => {
              const state = !editing ? taskState(idx) : "empty";
              return (
              <li key={idx}>
                {editing ? (
                  <div className="flex items-center gap-2">
                    <input
                      value={task}
                      onChange={(e) => {
                        const t = [...editForm.tasks];
                        t[idx] = e.target.value;
                        setEditForm({ ...editForm, tasks: t });
                      }}
                      data-testid={`edit-task-input-${idx}`}
                      className="flex-1 px-3 py-2 rounded-xl border border-slate-200 text-sm"
                    />
                    <button
                      onClick={() => setEditForm({ ...editForm, tasks: editForm.tasks.filter((_, i) => i !== idx) })}
                      data-testid={`edit-task-remove-${idx}`}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => toggleTask(idx)}
                    disabled={isStaff || state === "verified"}
                    data-testid={`task-toggle-${idx}`}
                    data-state={state}
                    title={state === "verified" ? "Verified by teacher" : state === "pending" ? "Pending teacher review — tap to undo submission" : "Tap to submit"}
                    className={`w-full flex items-start gap-3 text-left p-3 rounded-2xl border transition ${
                      state === "verified"
                        ? "bg-emerald-50 border-emerald-200 cursor-default"
                        : state === "pending"
                          ? "bg-amber-50 border-amber-200 hover:border-amber-300"
                          : "bg-white border-slate-200 hover:border-slate-300"
                    } ${isStaff ? "cursor-default" : ""}`}
                  >
                    {state === "verified" ? (
                      <CheckCircle weight="fill" size={24} className="text-emerald-500 mt-0.5 shrink-0" data-testid={`task-icon-verified-${idx}`} />
                    ) : state === "pending" ? (
                      <Clock weight="fill" size={24} className="text-amber-500 mt-0.5 shrink-0" data-testid={`task-icon-pending-${idx}`} />
                    ) : (
                      <Circle size={24} className="text-slate-300 mt-0.5 shrink-0" data-testid={`task-icon-empty-${idx}`} />
                    )}
                    <span className={`text-base flex-1 ${
                      state === "verified" ? "line-through text-slate-400" : "text-slate-800"
                    }`}>
                      {task}
                    </span>
                    {state === "pending" && (
                      <span className="text-[10px] font-bold tracking-wider uppercase text-amber-700 mt-1.5 shrink-0">
                        Pending review
                      </span>
                    )}
                  </button>
                )}
              </li>
              );
            })}
            {editing && (
              <li>
                <button
                  onClick={() => setEditForm({ ...editForm, tasks: [...editForm.tasks, "New task"] })}
                  data-testid="edit-task-add"
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-3 py-2"
                >
                  + Add task
                </button>
              </li>
            )}
          </ul>
        </div>

        {/* Sessions / lesson plan */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="lesson-plan-card">
          <h3 className="font-outfit font-bold text-xl mb-4">Lesson plan</h3>
          {editing ? (
            <ol className="space-y-3">
              {editForm.sessions.map((s, idx) => (
                <li
                  key={idx}
                  className="rounded-2xl border border-slate-200 p-3 bg-slate-50 grid grid-cols-1 sm:grid-cols-[110px_1fr_auto] gap-2 items-start"
                  data-testid={`edit-session-${idx}`}
                >
                  <input
                    value={s.time}
                    placeholder="10 min"
                    onChange={(e) => {
                      const next = [...editForm.sessions];
                      next[idx] = { ...next[idx], time: e.target.value };
                      setEditForm({ ...editForm, sessions: next });
                    }}
                    data-testid={`edit-session-time-${idx}`}
                    className="px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                  />
                  <div className="space-y-2">
                    <input
                      value={s.title}
                      placeholder="Activity title"
                      onChange={(e) => {
                        const next = [...editForm.sessions];
                        next[idx] = { ...next[idx], title: e.target.value };
                        setEditForm({ ...editForm, sessions: next });
                      }}
                      data-testid={`edit-session-title-${idx}`}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-outfit font-semibold bg-white"
                    />
                    <textarea
                      value={s.description}
                      placeholder="Describe what happens in this activity..."
                      rows={2}
                      onChange={(e) => {
                        const next = [...editForm.sessions];
                        next[idx] = { ...next[idx], description: e.target.value };
                        setEditForm({ ...editForm, sessions: next });
                      }}
                      data-testid={`edit-session-desc-${idx}`}
                      className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm bg-white"
                    />
                  </div>
                  <button
                    onClick={() =>
                      setEditForm({
                        ...editForm,
                        sessions: editForm.sessions.filter((_, i) => i !== idx),
                      })
                    }
                    data-testid={`edit-session-remove-${idx}`}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg justify-self-end"
                    title="Remove activity"
                  >
                    <X size={16} />
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={() =>
                    setEditForm({
                      ...editForm,
                      sessions: [
                        ...editForm.sessions,
                        { time: "10 min", title: "New activity", description: "" },
                      ],
                    })
                  }
                  data-testid="edit-session-add"
                  className="text-sm font-semibold text-slate-600 hover:text-slate-900 px-3 py-2"
                >
                  + Add activity
                </button>
              </li>
            </ol>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-2 space-y-5">
              {week.sessions.map((s, idx) => (
                <li key={idx} className="pl-5 relative">
                  <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-yellow-400 border-2 border-white shadow" />
                  <div className="text-xs font-bold tracking-wider uppercase text-slate-500">{s.time}</div>
                  <div className="font-outfit font-bold text-lg leading-tight">{s.title}</div>
                  <p className="text-sm text-slate-600 mt-1">{s.description}</p>
                </li>
              ))}
            </ol>
          )}
        </div>

        {/* Materials */}
        <div className="bg-white rounded-3xl p-6 border border-slate-200 shadow-sm" data-testid="materials-card">
          <h3 className="font-outfit font-bold text-lg mb-3">Materials</h3>
          {editing ? (
            <MaterialsEditor
              materials={editForm.materials}
              onChange={(materials) => setEditForm({ ...editForm, materials })}
            />
          ) : (
            <div className="flex flex-wrap gap-2">
              {week.materials.map((m, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 text-sm"
                  data-testid={`material-${idx}`}
                >
                  {m}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Success check */}
        <div className="bg-emerald-50 rounded-3xl p-6 border border-emerald-200" data-testid="success-check-card">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle weight="fill" className="text-emerald-600" size={22} />
            <h3 className="font-outfit font-bold text-lg">Success check</h3>
          </div>
          {editing ? (
            <input
              value={editForm.success_check}
              onChange={(e) => setEditForm({ ...editForm, success_check: e.target.value })}
              data-testid="edit-success-input"
              className="w-full bg-white px-3 py-2 rounded-xl border border-emerald-200 text-base"
            />
          ) : (
            <p className="text-emerald-900">{week.success_check}</p>
          )}
        </div>
      </main>
    </div>
  );
}
